import {
    getToken,
    handleAccountDeactivatedResponse,
    notifySessionExpired,
    refreshTokenIfNeeded,
    waitForPendingRefresh,
} from '../utils/auth';
import {
    NETWORK_ERROR_MESSAGE,
    TIMEOUT_ERROR_MESSAGE,
    UPLOAD_TIMEOUT_MS,
    createNetworkError,
    fetchWithTimeout,
} from '../utils/http';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const SESSION_EXPIRED_MESSAGE = 'Сессия истекла. Войдите снова.';
const ORDERS_MAX_LIMIT = 1000;

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    const branchId = localStorage.getItem('selectedBranchId');
    return {
        'Content-Type': 'application/json',
        ...(branchId && { 'X-Branch-ID': branchId }),
        ...(token && { 'Authorization': `Bearer ${token}` }),
    };
};

const getMultipartAuthHeaders = () => {
    const { 'Content-Type': _contentType, ...headers } = getAuthHeaders();
    return headers;
};

const getBearerToken = (headers) => {
    const value = headers?.Authorization || headers?.authorization;
    return value ? String(value).replace(/^Bearer\s+/i, '') : null;
};

/**
 * Единая точка всех запросов к API: таймаут, русские сетевые ошибки, продление сессии
 * и обработка 401. После 401 ответ возвращается вызывающему коду как есть, а поверх
 * текущей страницы открывается окно повторного входа.
 */
export const apiFetch = async (url, options = {}, fetchOptions = {}) => {
    refreshTokenIfNeeded();

    const response = await fetchWithTimeout(url, options, fetchOptions);
    if (response.status !== 401) {
        await handleAccountDeactivatedResponse(response);
        return response;
    }

    const usedToken = getBearerToken(options.headers);
    if (!usedToken) {
        return response;
    }

    // Запрос мог уйти со старым токеном, пока параллельно шло продление
    await waitForPendingRefresh();
    const currentToken = getToken();
    if (currentToken && currentToken !== usedToken) {
        const retried = await fetchWithTimeout(url, {
            ...options,
            headers: { ...options.headers, Authorization: `Bearer ${currentToken}` },
        }, fetchOptions);
        if (retried.status !== 401) {
            return retried;
        }
        notifySessionExpired();
        return retried;
    }

    notifySessionExpired();
    return response;
};

const buildQuery = (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            query.set(key, String(value));
        }
    });
    return query.toString();
};

const describeHttpFailure = (status, fallback) => {
    if (status === 401) return SESSION_EXPIRED_MESSAGE;
    if (status === 413) return 'Файл слишком большой для загрузки';
    if (status >= 502 && status <= 504) return 'Сервер временно недоступен. Повторите попытку позже.';
    return fallback;
};

const parseErrorDetail = (data) => {
    const detail = data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) {
        const field = detail[0].loc && detail[0].loc.length > 1 ? detail[0].loc[1] : 'неизвестное поле';
        return `${detail[0].msg} (поле: ${field})`;
    }
    return null;
};

/** Загрузка одного файла с прогрессом: fetch не умеет сообщать о ходе отправки. */
const uploadWithProgress = (url, file, { fieldName = 'files', onProgress, timeoutMs = UPLOAD_TIMEOUT_MS } = {}) => (
    new Promise((resolve, reject) => {
        refreshTokenIfNeeded();
        const headers = getMultipartAuthHeaders();
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append(fieldName, file, file.name);

        xhr.open('POST', url);
        xhr.timeout = timeoutMs;
        Object.entries(headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));

        if (onProgress && xhr.upload) {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable && event.total > 0) {
                    onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
                }
            };
        }

        xhr.onerror = () => reject(createNetworkError(NETWORK_ERROR_MESSAGE));
        xhr.ontimeout = () => reject(createNetworkError(TIMEOUT_ERROR_MESSAGE));
        xhr.onload = () => {
            let data = null;
            try {
                data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch {
                data = null;
            }

            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(data);
                return;
            }

            if (xhr.status === 401) {
                notifySessionExpired();
            }
            const error = new Error(
                xhr.status === 401
                    ? SESSION_EXPIRED_MESSAGE
                    : parseErrorDetail(data) || describeHttpFailure(xhr.status, 'Не удалось загрузить файл')
            );
            error.status = xhr.status;
            reject(error);
        };

        xhr.send(formData);
    })
);

const handleResponse = async (response) => {
    // Проверяем статус ответа
    // Если 204 No Content, просто возвращаем null или пустой объект
    if (response.status === 204) {
        return null;
    }

    if (response.status === 401) {
        const error = new Error(SESSION_EXPIRED_MESSAGE);
        error.status = 401;
        throw error;
    }

    const responseClone = response.clone();

    // Пытаемся получить JSON из ответа
    let data;
    try {
        data = await response.json();
    } catch (e) {
        const text = await responseClone.text().catch(() => '');
        const normalizedText = String(text || '').trim();
        const shortPreview = normalizedText.slice(0, 120).replace(/\s+/g, ' ');
        const htmlLikeResponse = shortPreview.startsWith('<!DOCTYPE') || shortPreview.startsWith('<html');

        if (!response.ok && (htmlLikeResponse || !normalizedText)) {
            const error = new Error(describeHttpFailure(
                response.status,
                `Не удалось выполнить запрос. Статус: ${response.status}`
            ));
            error.status = response.status;
            throw error;
        }

        if (htmlLikeResponse) {
            throw new Error(
                `API вернул HTML вместо JSON (статус ${response.status}). Проверьте proxy в frontend/package.json или REACT_APP_API_URL.`
            );
        }

        if (normalizedText) {
            const error = new Error(normalizedText);
            error.status = response.status;
            throw error;
        }

        throw new Error(`Не удалось прочитать ответ сервера. Статус: ${response.status}`);
    }
    
    // Если ответ не успешный, формируем понятную ошибку
    if (!response.ok) {
        let errorMessage = 'Произошла ошибка при выполнении запроса';
        
        if (data && data.detail) {
            if (typeof data.detail === 'string') {
                errorMessage = data.detail;
            } else if (Array.isArray(data.detail) && data.detail.length > 0) {
                // Формируем сообщение из первой ошибки валидации
                const firstError = data.detail[0];
                if (firstError.msg) {
                    const fieldName = firstError.loc && firstError.loc.length > 1 
                        ? firstError.loc[1] : 'неизвестное поле';
                    errorMessage = `${firstError.msg} (поле: ${fieldName})`;
                }
            }
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
    }
    
    return data;
};

export const branchesAPI = {
    getAll: async () => {
        const response = await apiFetch(`${API_BASE_URL}/branches/`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
};

// Clients API
export const clientsAPI = {
    getAll: async (params = {}) => {
        const normalizedParams = { ...params };
        if (normalizedParams.limit !== undefined) {
            const parsedLimit = Number(normalizedParams.limit);
            if (Number.isFinite(parsedLimit)) {
                normalizedParams.limit = String(Math.min(Math.max(Math.trunc(parsedLimit), 1), 500));
            }
        }

        const queryString = new URLSearchParams(normalizedParams).toString();
        const url = queryString
            ? `${API_BASE_URL}/clients/?${queryString}`
            : `${API_BASE_URL}/clients/`;
        const response = await apiFetch(url, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    // Серверный поиск: без него фильтруются только первые записи страницы
    search: async (q = '', { skip = 0, limit = 100 } = {}) => {
        const query = buildQuery({ q: String(q || '').trim(), skip, limit: Math.min(limit, 500) });
        const response = await apiFetch(`${API_BASE_URL}/clients/?${query}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/clients/${id}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/clients/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await apiFetch(`${API_BASE_URL}/clients/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/clients/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
};

// Dealerships API
export const dealershipsAPI = {
    getAll: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const url = queryString
            ? `${API_BASE_URL}/dealerships/?${queryString}`
            : `${API_BASE_URL}/dealerships/`;
        const response = await apiFetch(url, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    // Сводка для списка: заказы месяца, открытые заказы, последний заказ, работы месяца
    getOverview: async (period) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/overview?${buildQuery({ period })}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/${id}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getManagers: async (dealershipId) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/${dealershipId}/managers`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    createManager: async (dealershipId, data) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/${dealershipId}/managers`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    updateManager: async (dealershipId, managerId, data) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/${dealershipId}/managers/${managerId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    deleteManager: async (dealershipId, managerId) => {
        const response = await apiFetch(`${API_BASE_URL}/dealerships/${dealershipId}/managers/${managerId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    uploadLogo: async (dealershipId, file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await apiFetch(`${API_BASE_URL}/dealerships/${dealershipId}/upload-logo`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData,
        });
        return handleResponse(response);
    },

    getCashbackBreakdown: async (dealershipId, period, year) => {
        const params = new URLSearchParams({ period: String(period), year: String(year) });
        const response = await apiFetch(`${API_BASE_URL}/dealerships/${dealershipId}/cashback-breakdown?${params.toString()}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
};

// Vehicles API
export const vehiclesAPI = {
    getAll: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const url = queryString
            ? `${API_BASE_URL}/vehicles/?${queryString}`
            : `${API_BASE_URL}/vehicles/`;
        const response = await apiFetch(url, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/vehicles/${id}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/vehicles/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await apiFetch(`${API_BASE_URL}/vehicles/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    uploadPhoto: async (id, file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await apiFetch(`${API_BASE_URL}/vehicles/${id}/upload-photo`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData,
        });
        return handleResponse(response);
    },

    uploadPhotos: async (id, files) => {
        const formData = new FormData();
        (files || []).forEach((file) => {
            formData.append('files', file);
        });

        const response = await apiFetch(`${API_BASE_URL}/vehicles/${id}/upload-photos`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData,
        });
        return handleResponse(response);
    },

    uploadPhotoFile: (id, file, options = {}) => (
        uploadWithProgress(`${API_BASE_URL}/vehicles/${id}/upload-photos`, file, options)
    ),

    deletePhoto: async (id, photoUrl) => {
        const params = new URLSearchParams({ photo_url: photoUrl });
        const response = await apiFetch(`${API_BASE_URL}/vehicles/${id}/photos?${params.toString()}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/vehicles/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    // Новый метод для получения автомобилей по ID клиента
    getByClient: async (clientId) => {
        const response = await apiFetch(`${API_BASE_URL}/vehicles/?client_id=${clientId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getBrandSuggestions: async (q = '', limit = 20) => {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        params.set('limit', String(limit));
        const response = await apiFetch(`${API_BASE_URL}/vehicles/brands?${params.toString()}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getModelSuggestions: async (brand = '', q = '', limit = 30) => {
        const params = new URLSearchParams();
        if (brand) params.set('brand', brand);
        if (q) params.set('q', q);
        params.set('limit', String(limit));
        const response = await apiFetch(`${API_BASE_URL}/vehicles/models?${params.toString()}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
};

// Orders API
export const ordersAPI = {
    getAll: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const url = queryString
            ? `${API_BASE_URL}/orders/?${queryString}`
            : `${API_BASE_URL}/orders/`;
        const response = await apiFetch(url, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    // Страница заказов, новые первыми: { skip, limit, date_from, date_to, ... }
    getPage: async ({ skip = 0, limit = 500, ...filters } = {}) => {
        const query = buildQuery({ ...filters, skip, limit: Math.min(limit, ORDERS_MAX_LIMIT) });
        const response = await apiFetch(`${API_BASE_URL}/orders/?${query}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getHistory: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/history`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    createStatusLink: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/status-link`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    // При отправке данных на сервер убедитесь, что дата в ISO формате
    create: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    uploadBeforePhotos: async (id, files) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/before-photos`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData,
        });
        return handleResponse(response);
    },

    uploadAfterPhotos: async (id, files) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/after-photos`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData,
        });
        return handleResponse(response);
    },

    uploadDamagePhotos: async (id, files) => {
        const formData = new FormData();
        (files || []).forEach(file => formData.append('files', file));

        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/damage-photos`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData,
        });
        return handleResponse(response);
    },

    uploadDamagePhotoFile: (id, file, options = {}) => (
        uploadWithProgress(`${API_BASE_URL}/orders/${id}/damage-photos`, file, options)
    ),

    deleteDamagePhoto: async (id, photoUrl) => {
        const params = new URLSearchParams({ photo_url: photoUrl });
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/damage-photos?${params.toString()}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    uploadDocuments: async (id, files) => {
        const formData = new FormData();
        (files || []).forEach(file => formData.append('files', file));

        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/documents`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData,
        });
        return handleResponse(response);
    },

    deleteDocument: async (id, fileUrl) => {
        const params = new URLSearchParams({ file_url: fileUrl });
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/documents?${params.toString()}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    requestDiscount: async (id, data) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/discount/request`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    approveDiscount: async (id, data = { approve: true }) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/discount/approve`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    refreshUsdRateSnapshot: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/orders/${id}/refresh-usd-rate`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
};

// Users API
export const usersAPI = {
    getAll: async (params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        const response = await apiFetch(`${API_BASE_URL}/users/?${queryParams}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/users/${id}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/users/`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await apiFetch(`${API_BASE_URL}/users/${id}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/users/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getMasters: async () => {
        const response = await apiFetch(`${API_BASE_URL}/users/masters/`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    }
};

// Schedules API
export const schedulesAPI = {
    getAll: async (params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        const url = queryParams
            ? `${API_BASE_URL}/schedules/?${queryParams}`
            : `${API_BASE_URL}/schedules/`;
        const response = await apiFetch(url, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/schedules/`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/schedules/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
};

// Reminders API
export const remindersAPI = {
    getAll: async (params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        const url = queryParams
            ? `${API_BASE_URL}/reminders/?${queryParams}`
            : `${API_BASE_URL}/reminders/`;
        const response = await apiFetch(url, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/reminders/`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await apiFetch(`${API_BASE_URL}/reminders/${id}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/reminders/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
};

// Personal push settings: event groups of the user's role and quiet hours
export const notificationsAPI = {
    getPreferences: async () => {
        const response = await apiFetch(`${API_BASE_URL}/notifications/preferences`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    updatePreferences: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/notifications/preferences`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },
};

// Services API
export const servicesAPI = {
    getAll: async (includeInactive = false) => {
        const params = new URLSearchParams();
        if (!includeInactive) {
            params.append('is_active', 'true');
        }
        
        const response = await apiFetch(`${API_BASE_URL}/services/?${params}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/services/${id}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await apiFetch(`${API_BASE_URL}/services/`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await apiFetch(`${API_BASE_URL}/services/${id}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/services/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    // Метод для получения услуг для заказов
    getForOrders: async () => {
        const response = await apiFetch(`${API_BASE_URL}/services/for-orders`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
};

// Currency Rates API
export const currencyRatesAPI = {
    getAll: async () => {
        const response = await apiFetch(`${API_BASE_URL}/currency-rates/`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getCurrent: async () => {
        const response = await apiFetch(`${API_BASE_URL}/currency-rates/current`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    getHistory: async (limit = 50) => {
        const response = await apiFetch(`${API_BASE_URL}/currency-rates/history?limit=${limit}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    // Курс из внешнего сервиса: ключ хранится на сервере, браузер его не знает
    getLive: async () => {
        const response = await apiFetch(`${API_BASE_URL}/currency-rates/live`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    updateFromAPI: async () => {
        const response = await apiFetch(`${API_BASE_URL}/currency-rates/update-from-api`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    setManualRate: async ({ from_currency, rate }) => {
        // Сервер принимает форму, а не JSON
        const body = new URLSearchParams({ from_currency, rate: String(rate) });
        const response = await apiFetch(`${API_BASE_URL}/currency-rates/manual`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body,
        });
        return handleResponse(response);
    },

    delete: async (rateId) => {
        const response = await apiFetch(`${API_BASE_URL}/currency-rates/${rateId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    }
};

// Accounting API
const jsonRequest = async (path, method = 'GET', body) => {
    const response = await apiFetch(`${API_BASE_URL}${path}`, {
        method,
        headers: body === undefined ? getAuthHeaders() : { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    return handleResponse(response);
};

// Плёнка роликами
export const materialRollsAPI = {
    list: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return jsonRequest(`/material-rolls${query ? `?${query}` : ''}`);
    },
    deliver: (payload) => jsonRequest('/material-rolls/deliveries', 'POST', payload),
    update: (rollId, payload) => jsonRequest(`/material-rolls/${rollId}`, 'PATCH', payload),
    setStatus: (rollId, status) => jsonRequest(`/material-rolls/${rollId}/status`, 'POST', { status }),
    pay: (rollIds, paid = true, paidOn) => jsonRequest('/material-rolls/pay', 'POST', {
        roll_ids: rollIds, paid, ...(paidOn ? { paid_on: paidOn } : {}),
    }),
    remove: (rollId) => jsonRequest(`/material-rolls/${rollId}`, 'DELETE'),
    rollUsage: (rollId) => jsonRequest(`/material-rolls/${rollId}/usage`),
    orderUsage: (orderId) => jsonRequest(`/material-rolls/order/${orderId}`),
    addUsage: (rollId, payload) => jsonRequest(`/material-rolls/${rollId}/usage`, 'POST', payload),
    removeUsage: (usageId) => jsonRequest(`/material-rolls/usage/${usageId}`, 'DELETE'),
};

export const accountingAPI = {
    getByPeriod: async (period) => {
        const response = await apiFetch(`${API_BASE_URL}/accounting/periods/${period}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    upsertPeriod: async (period, data) => {
        const response = await apiFetch(`${API_BASE_URL}/accounting/periods/${period}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        return handleResponse(response);
    },

    getExpenseCategories: async (expenseType) => {
        const query = expenseType ? `?expense_type=${expenseType}` : '';
        const response = await apiFetch(`${API_BASE_URL}/accounting/expense-categories${query}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    createExpenseCategory: async (payload) => {
        const response = await apiFetch(`${API_BASE_URL}/accounting/expense-categories`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        return handleResponse(response);
    },

    deleteExpenseCategory: async (categoryId) => {
        const response = await apiFetch(`${API_BASE_URL}/accounting/expense-categories/${categoryId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    // Бухгалтерия по шаблону отделения
    getSheet: (period) => jsonRequest(`/accounting/sheet?period=${encodeURIComponent(period)}`),
    setSheetValue: (period, key, value, comment) => jsonRequest(
        `/accounting/sheet/${period}/values/${key}`, 'PUT', { value, comment },
    ),
    addJournalEntry: (period, key, payload) => jsonRequest(`/accounting/sheet/${period}/journal/${key}`, 'POST', payload),
    updateJournalEntry: (id, payload) => jsonRequest(`/accounting/journal/${id}`, 'PATCH', payload),
    deleteJournalEntry: (id) => jsonRequest(`/accounting/journal/${id}`, 'DELETE'),
    closeMonth: (period) => jsonRequest(`/accounting/sheet/${period}/close`, 'POST'),
    reopenMonth: (period) => jsonRequest(`/accounting/sheet/${period}/close`, 'DELETE'),
    getTemplates: () => jsonRequest('/accounting/templates'),
    getTemplateEditor: (period) => jsonRequest(`/accounting/templates/editor?period=${encodeURIComponent(period)}`),
    getTemplateOptions: () => jsonRequest('/accounting/templates/options'),
    saveTemplate: (validFrom, fields) => jsonRequest('/accounting/templates', 'PUT', { valid_from: validFrom, fields }),
    previewTemplate: (period, fields) => jsonRequest('/accounting/templates/preview', 'POST', { period, fields }),
    createTemplateFromPreset: (preset, validFrom) => jsonRequest(
        '/accounting/templates/preset', 'POST', { preset, valid_from: validFrom },
    ),

    // Журнал расходов: каждая запись создаётся, меняется и удаляется отдельно
    getExpenses: async (period) => {
        const response = await apiFetch(`${API_BASE_URL}/accounting/expenses?period=${encodeURIComponent(period)}`, {
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },

    createExpense: async (payload) => {
        const response = await apiFetch(`${API_BASE_URL}/accounting/expenses`, {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        return handleResponse(response);
    },

    updateExpense: async (id, payload) => {
        const response = await apiFetch(`${API_BASE_URL}/accounting/expenses/${id}`, {
            method: 'PATCH',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        return handleResponse(response);
    },

    deleteExpense: async (id) => {
        const response = await apiFetch(`${API_BASE_URL}/accounting/expenses/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleResponse(response);
    },
    };

    // Masters API
    export const mastersAPI = {
        getStatistics: async (masterId, month = null) => {
            const params = new URLSearchParams();
            if (month) {
                params.append('month', month);
            }
            const queryString = params.toString();
            const url = queryString
                ? `${API_BASE_URL}/masters/${masterId}/statistics?${queryString}`
                : `${API_BASE_URL}/masters/${masterId}/statistics`;
        
            const response = await apiFetch(url, {
                headers: getAuthHeaders(),
            });
            return handleResponse(response);
        },

        getWorkload: async (masterId, month = null) => {
            const params = new URLSearchParams();
            if (month) {
                params.append('month', month);
            }
            const queryString = params.toString();
            const url = queryString
                ? `${API_BASE_URL}/masters/${masterId}/workload?${queryString}`
                : `${API_BASE_URL}/masters/${masterId}/workload`;
        
            const response = await apiFetch(url, {
                headers: getAuthHeaders(),
            });
            return handleResponse(response);
        }
};